import { useEffect, useRef } from 'react';
import { HelpCircle } from 'lucide-react';
import { startContinuousBeep } from '@/utils/soundEffects';

interface AgeVerificationModalProps {
  isOpen: boolean;
  itemName: string;
  minimumAge: number;
  onConfirm: (birthYear: number) => void;
  onCancel: () => void;
}

export default function AgeVerificationModal({
  isOpen,
  itemName,
  minimumAge,
  onConfirm,
  onCancel,
}: AgeVerificationModalProps) {
  const beepRef = useRef<{ stop: () => void } | null>(null);

  // Sound loop: continuous warning beep until YES or NO is clicked
  useEffect(() => {
    if (isOpen) {
      beepRef.current = startContinuousBeep(550);
    }
    return () => {
      if (beepRef.current) {
        beepRef.current.stop();
        beepRef.current = null;
      }
    };
  }, [isOpen]);

  // Handle keyboard shortcuts (Y / Enter -> YES, N / Escape -> NO)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'y' || e.key === 'Y' || e.key === 'Enter') {
        e.preventDefault();
        handleYes();
      } else if (e.key === 'n' || e.key === 'N' || e.key === 'Escape') {
        e.preventDefault();
        handleNo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, minimumAge]);

  if (!isOpen) return null;

  const currentYear = new Date().getFullYear();
  const calculatedBirthYear = currentYear - (minimumAge || 18);

  const handleYes = () => {
    if (beepRef.current) {
      beepRef.current.stop();
      beepRef.current = null;
    }
    onConfirm(calculatedBirthYear);
  };

  const handleNo = () => {
    if (beepRef.current) {
      beepRef.current.stop();
      beepRef.current = null;
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150">
      <div
        className="w-full max-w-[480px] bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200/80 animate-in zoom-in-95 duration-150"
        role="dialog"
        aria-modal="true"
      >
        {/* Blue Header Bar */}
        <div className="bg-[#3B82F6] px-6 py-3.5 text-white flex items-center justify-between">
          <h3 className="font-semibold text-base text-white tracking-wide">
            Age Restriction
          </h3>
        </div>

        {/* Content Body */}
        <div className="p-6 bg-white flex items-center gap-4">
          {/* Blue Question Mark Circular Badge */}
          <div className="w-12 h-12 rounded-full bg-gradient-to-b from-[#4A90E2] to-[#1E62D0] border-2 border-white shadow-md flex items-center justify-center text-white shrink-0">
            <HelpCircle className="w-8 h-8 text-white drop-shadow" />
          </div>

          {/* Prompt Question */}
          <div className="flex-1">
            <p className="text-[17px] text-slate-800 font-medium leading-snug">
              Age Restriction: {minimumAge || 18}+ years. Is Customer Eligible for this Product?
            </p>
            {itemName && (
              <p className="text-xs text-slate-500 mt-1 font-normal">
                Item: <span className="font-semibold text-slate-700">{itemName}</span>
              </p>
            )}
          </div>
        </div>

        {/* Footer with YES / NO buttons */}
        <div className="bg-[#F1F3F5] px-6 py-4 flex items-center justify-end border-t border-slate-200/60">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleYes}
              className="min-w-[90px] px-6 py-2 bg-[#2E9B64] hover:bg-[#268555] active:bg-[#1E6F45] text-white font-bold text-sm uppercase tracking-wider rounded-lg shadow-xs transition-all focus:outline-none focus:ring-2 focus:ring-[#2E9B64]/50 cursor-pointer"
            >
              YES
            </button>
            <button
              type="button"
              onClick={handleNo}
              className="min-w-[90px] px-6 py-2 bg-[#C00000] hover:bg-[#A30000] active:bg-[#850000] text-white font-bold text-sm uppercase tracking-wider rounded-lg shadow-xs transition-all focus:outline-none focus:ring-2 focus:ring-[#C00000]/50 cursor-pointer"
            >
              NO
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
